// Japanese translation. Must satisfy the Messages shape derived from en.ts.

import type { Messages } from "./en";

export const ja: Messages = {
  appMenu: {
    undo: "元に戻す",
    redo: "やり直す",
  },
  toolbar: {
    model: "機種",
    rate: "レート",
    new: "新規",
    file: "ファイル",
    open: "開く",
    openSettings: "設定ファイルを取り込み (実験的)",
    save: "保存",
    exportPng: "PNG 出力",
    exportPdf: "PDF 出力",
    viewGraph: "グラフ",
    viewConsole: "コンソール",
    viewGraphHint: "ノードグラフのルーティング表示",
    viewConsoleHint: "ミキサー型のレベル一覧",
    viewSim: "SIM",
    viewSimHint: "本体の LCD 画面 (同じ計画を表示・編集)",
    simScale: (percent: number): string => `SIM Scale ${percent}%`,
    view: "表示",
    viewHint: "レイアウト・表示オプション",
    arrange: "整列",
    hideUnused: "未接続を隠す",
    showOffSends: "OFF send を表示",
    hideOffSends: "OFF send を隠す",
    hideOffSendsHint: "-∞ や OFF の send を隠し、有効な経路だけを表示する",
    labelsModel: "標準ラベル",
    labelsDevice: "デバイス名",
    labelsHint: "キャンバスのラベル: ツール標準のチャンネル名 (CH 1) かデバイスで設定した名前 (ch 1)",
    device: "デバイス",
    fetchDevice: "デバイスから取得",
    fetchCancel: "取得を中止",
    writeDevice: "デバイスへ書き込み",
    writeCancel: "書き込みを中止",
    compare: "デバイスと照合 (実験的)",
    compareCancel: "照合を中止",
    selfTest: "セルフテスト (実験的)",
    selfTestCancel: "セルフテストを中止",
    liveSync: "ライブ同期",
    liveSyncHint: "編集を接続中のデバイスへ即時反映する",
    liveTag: "LIVE",
    desktopApp: "デスクトップ版",
    desktopAppHint: "デスクトップ版を入手 — 計画の保存・読込、画像出力、デバイスのライブ制御に対応",
    shareUrl: "共有 URL",
    shareUrlHint: "この計画を共有できるリンクをコピー (アドレスバーにも反映)",
    downloadJson: "JSON ダウンロード",
    downloadJsonHint: "この計画を JSON でダウンロード — デスクトップ版で開けます",
    followUsb: "FOLLOW USB",
    followUsbOnHint:
      "デバイスは USB ホストからクロックを受け取っているため、左のレートをデバイス側で変更できません。クリックすると Follow USB を OFF にします。",
    followUsbOffHint:
      "デバイスは自身のクロックで動作しているため、左のレートがそのまま動作レートです。クリックするとクロックを USB ホストに委ねます。",
    followUsbUnknownHint:
      "デバイスが USB ホストからクロックを受け取っているかは未取得です。左のレートが実際の動作レートとは限りません。クリックするとデバイスから読み取ります。",
  },
  console: {
    mute: "MUTE",
    // Accessible name suffix for the scribble power LED (the node master on/off).
    power: "オン/オフ",
    sideShowing: (pair: string, shown: string) => `${pair}、${shown} を表示中`,
    gainShared: (others: string) => `${others} と共有`,
    clipSafeLabel: "SAFE",
    eq: "EQ",
    effect: "EFFECT",
    effectHint:
      "本体にはこのエフェクト単体の ON/OFF がなく、常に掛かっています。FX チャンネル自体の ON/OFF はストリップの電源ボタンで切り替えます。",
    pre: "PRE",
    sends: "SENDS",
    sendPan: "SEND PAN",
    preHint: "Pre-fader send",
    cue: "C.INT",
    cueFull: "Cue Interrupt",
    cueChip: "CUE",
    cueOffline: "CUE はライブ同期中に本体の CUE を切り替えます。",
    cueTaking: "Cue Interrupt — 何かを CUE している間、このモニターは元の音の代わりに CUE バスを流します",
    mono: "MONO",
    // Strip group separators (this master and the group* keys below) and the readout captions
    // (readFader / readMeter). The separators are set in vertical writing mode, where a full-width
    // glyph changes the column's width itself, and the captions follow the separators, so no single
    // word on a strip is Japanese. English in every language. The same item on the tuning screens
    // is translated as 「メーター」 (readouts below).
    master: "MASTER",
    meterPoint: "METER POINT",
    meterPointHint: "実機がメーターを出すタップ点のみ表示。",
    readFader: "FADER",
    readMeter: "METER",
    groupInputs: "INPUTS",
    groupBus: "BUS / FX",
    groupMon: "MONITOR",
    // The INS FX chip's popover. The heading and the switch are the device's words, so they
    // are not translated.
    insFxType: "EFFECT TYPE",
    insFxInUse: "使用中",
    insFxMax: (maxRate: string): string => `上限 ${maxRate}`,
    insFxMonoOnly: "MONO 専用",
    insFxRemove: "解除",
    insFxPickFirst: "先に上のリストからエフェクトを選んでください。",
  },
  // SETUP > GENERAL on the unit. Section and control names stay in English in the Japanese
  // user guide too, so they are not translated; only the explanatory notes are in Japanese.
  // Matching the unit's own wording is what lets a row on this screen be read as the menu on
  // the hardware.
  deviceSetup: {
    menuItem: "本体設定",
    title: "本体設定",
    close: "閉じる",
    apply: "本体へ適用",
    pending: (n: number): string => `未適用の変更 ${n} 件`,
    standingNote: "変更は「本体へ適用」を押すまで本体に書き込まれません。計画ファイルには含まれません。",
    onlyOn: (models: string): string => `${models} のみ`,
    languageSection: "Language",
    displayLanguage: "表示言語",
    languageNote:
      "書き込んだ値は保持されますが、本体画面への反映は本体で Language を一度切り替えるまで遅れることがあります。",
    brightnessSection: "Brightness",
    screen: "Screen",
    powerSection: "Power Management",
    autoPowerOff: "Auto Power Off",
    enable: "Enable",
    time: "Time",
    minutes: (n: number): string => `${n} 分`,
    dateTimeSection: "Date/Time",
    timeZone: "Time Zone",
    displayFormat: "Display Format",
    date: "Date",
    clockNote:
      "本体の時計は microSD 録音の日時に使われます。時計そのものは本体のパネルからのみ設定でき、USB 接続したコンピューターの時刻にも同期しません。",
    timeZoneNote:
      "都市の一覧は本体を部分的に観測して再現したものです。項目が実際と異なる可能性があるため、適用後に本体の表示を確認してください。",
    noDateTime: "URX22 は microSD を持たないため、本体に Date/Time メニューがありません。",
    peripheralSection: "Peripheral",
    usbMain: "USB Main",
    usbSuppression: "Generic Driver Audio Channel Suppression",
    usbNote:
      "iPad / iPhone などの標準ドライバーで接続したホストに提供するチャンネル数の制限です。2 Channels で 2 IN / 2 OUT に制限されます。",
    hdmi: "HDMI",
    hdcp: "HDCP",
    hdmiChannels: "Input Audio Channels",
    hdmiNote:
      "2 Channels は常に 2 チャンネル (最大 48 kHz)、Multi Channels は最大 192 kHz / 8 チャンネルまで対応し、ミキサー内部でステレオへダウンミックスされます。",
    hdmiOnly: "HDMI は URX44V のみの装備です。",
    knobsSection: "User Defined Knobs",
    bank: (n: number): string => `BANK ${n}`,
    function: "Function",
    param1: "Parameter 1",
    param2: "Parameter 2",
    knobsNote: "BANK は本体のバンク切替と同じ単位です。",
    unset: "—",
    unknownValue: (v: number | string): string => `不明 (${v})`,
  },
  midi: {
    menuItem: "MIDI コントロール",
    title: "MIDI コントロール",
    ports: "ポート",
    input: "入力",
    output: "出力",
    portNone: "なし",
    learn: "ラーン",
    colControl: "コントロール",
    colAddr: "アドレス",
    colOption: "挙動",
    hintIdle:
      "ラーンをオンにしてコンソールまたは調整画面のコントロールをクリックし、MIDI 機器のコントロールを動かします。",
    hintLearn: "割り当てるコンソールまたは調整画面のコントロールをクリックしてください。",
    hintArmed: (control: string): string => `${control} に割り当てる MIDI コントロールを動かしてください…`,
    learnOff: "ラーンをオフにしました: コンソールと調整画面は再び値を編集します。",
    mappings: "割り当て",
    noMappings: "割り当てはまだありません。",
    remove: "割り当てを削除",
    // Shown on a row that shares its MIDI control with an earlier assignment (linked): they move
    // together, and feedback follows the first assignment.
    linked: "連動",
    inactiveNoSource: (channel: string): string => `無効 — ${channel} にソースがありません`,
    inactiveNoSwitch: (channel: string, control: string): string =>
      `無効 — ${channel} の入力に ${control} がありません`,
    linkedHint:
      "このアドレスの最初の割り当てと 1 つの MIDI コントロールを共有します。一緒に動き、MIDI フィードバックは最初の割り当てに追従します。",
    // The option labels stay in English (katakana such as 「アブソリュート」 does not fit the
    // fixed-width select, and other MIDI mapping tools use the same vocabulary).
    // Only the behavior notes (the *Desc keys below) are in Japanese. The stored values stay
    // "edge" / "state" for compatibility.
    // The button modes are named after the sender's button type (a controller, Stream Deck, etc.):
    // a momentary button (value on press, 0 on release) = edge, a toggle button (alternating
    // 127/0) = state.
    mode: { absolute: "Absolute", pickup: "Pickup" },
    buttonMode: { edge: "Momentary", state: "Toggle" },
    // Names the setting a select controls: used for its tooltip, the first half of its
    // aria-label, and the heading over that vocabulary in the legend under the table.
    modeTitle: "取り込みモード",
    buttonModeTitle: "ボタン動作",
    // Behavior notes, printed in that legend for each vocabulary the list uses
    // (a native dropdown cannot annotate its own options).
    modeDesc: {
      absolute: "受信値をそのまま位置として適用。物理位置と盤面値がずれていると値が飛ぶ。",
      pickup: "物理値が盤面値に追いつく (通過する) まで無視。値は飛ばないが、追いつくまで効かない。",
    },
    buttonModeDesc: {
      edge: "プッシュ / モーメンタリーボタン向け: 押すたびに 1 回反転し、離した信号 (0) は無視する。ボタンが on 値のみ送り 0 を送らなくても毎回反転する。",
      state:
        "トグルボタン (押すたび 127/0 交互送信、Stream Deck 等) 向け: 値がそのまま状態になる — 64 以上で ON、未満で OFF。モーメンタリーボタンなら押している間だけ ON。",
    },
    bound: (control: string, addr: string): string => `${addr} を ${control} に割り当てました`,
    learnKindMismatch: (control: string, addr: string): string =>
      `割り当てません: ${addr} は ${control} と種類の違うコントロールを既に動かしています — スイッチと連続値のコントロールは 1 つの MIDI コントロールを共有できません`,
    learnUnresolved: (control: string, addr: string): string =>
      `割り当てません: ${control}、または ${addr} が既に動かしているものが現在のプランにありません`,
    mixedGangAbsolute: (addrs: string): string =>
      `${addrs} の取り込みモードを Absolute にしました: スイッチと連続値のコントロールが共有しており、スイッチの後ろでは Pickup が効きません`,
    windowError: (message: string): string => `MIDI コントロールウィンドウを開けませんでした: ${message}`,
    inputError: (message: string): string => `MIDI 入力エラー: ${message}`,
    outputError: (message: string): string => `MIDI 出力エラー: ${message}`,
    outputStalled:
      "MIDI フィードバックを停止しました — 出力ポートがメッセージを受け付け続けませんでした。コントローラーを接続し直し、ポートを選び直してください。",
    // Control names reuse the console strip's wording (chips / knobs) as is.
    param: {
      level: "Level",
      mute: "MUTE",
      chOn: "ON",
      pan: "PAN/BAL",
      tap: "PRE",
      gain: "GAIN",
      phonesLevel: "PHONES",
      oscOn: "ON",
      cueInterrupt: "C.INT",
      cue: "CUE",
      mono: "MONO",
      gateOn: "GATE",
      compOn: "COMP",
      eqOn: "EQ",
      phantom: "+48",
      phase: "Ф",
      phaseL: "ФL",
      phaseR: "ФR",
      hpf: "HPF",
      hiZ: "Hi-Z",
      duckerOn: "DUCKER",
      insertFxOn: "INS FX",
      threshold: "Threshold",
      range: "Range",
      attack: "Attack",
      hold: "Hold",
      decay: "Decay",
      ratio: "Ratio",
      release: "Release",
      autoMakeup: "Auto Makeup",
      oneKnob: "1-knob",
      oneKnobLevel: "1-knob Level",
      freq: "Freq",
      q: "Q",
      bandOn: "Band ON",
      ssmcsOn: "SSMCS",
      compDrive: "Comp Drive",
      morphing: "Morphing",
      outGain: "Out Gain",
      sideChain: "Side Chain",
    },
    scopedParam: { gain: "Gain" } as Record<string, string>,
    paramSpoken: { phase: "フェーズ", phaseL: "フェーズ L", phaseR: "フェーズ R" } as Record<string, string>,
    scope: {
      gate: "GATE",
      comp: "COMP",
      eq: "EQ 1-knob",
      "eq.low": "EQ LOW",
      "eq.lowMid": "EQ LOW-MID",
      "eq.highMid": "EQ HIGH-MID",
      "eq.high": "EQ HIGH",
      ducker: "DUCKER",
      ssmcs: "SSMCS",
      "ssmcs.comp": "SSMCS COMP",
      "ssmcs.sc": "SSMCS Side Chain",
      "ssmcs.eq.low": "SSMCS EQ LOW",
      "ssmcs.eq.mid": "SSMCS EQ MID",
      "ssmcs.eq.high": "SSMCS EQ HIGH",
    } as Record<string, string>,
  },
  inspector: {
    title: "インスペクタ",
    close: "閉じる",
    hint:
      "ノードをドラッグして配置し、出力ポート (右) から入力ポート (左) へ" +
      "ドラッグして結線します。接続可能なポートは結線中に緑でハイライトされます。" +
      "チャンネルのダイレクト出力・録音は、上辺の Rec Point タップから配線します。" +
      "ノード右上のペンでノートを追加し、ノートをクリックすると編集できます。" +
      "キーボードでは Tab で盤面に入り、矢印キーでノード間を移動し、Enter で選択します。" +
      "選択したノードのルーティング欄からポートを結線し、結線を選択できます。",
    type: "種別",
    // Rows the unit's CH SETTING screen carries. The unit keeps them in English with its display
    // language set to Japanese, so they are not translated.
    name: "Name",
    color: "Color",
    colorName: {
      blue: "Blue",
      orange: "Orange",
      yellow: "Yellow",
      purple: "Purple",
      cyan: "Cyan",
      magenta: "Magenta",
      red: "Red",
      green: "Green",
      ltGreen: "LtGreen",
      white: "White",
      off: "Off",
    },
    recPoint: "Rec Point",
    inputsFrom: (n: number): string => `接続元から (${n})`,
    outputsTo: (n: number): string => `接続先へ (${n})`,
    routing: "ルーティング",
    connectSource: "ソースを接続",
    connectOutput: "出力の接続先",
    connectRecPoint: "Rec Point の接続先",
    connectChoose: "選択…",
    connection: "接続",
    from: "元",
    to: "先",
    parameters: "パラメータ",
    inputSection: "入力",
    level: "Level",
    pan: "Pan",
    balance: "Balance",
    prePost: "PRE/POST",
    prePostLcdOnly: "変更できません (本体が受け付けません)",
    eqRateLocked: "176.4 / 192 kHz ではステレオ ch の EQ は無効 — 強制的に OFF。",
    insFxRateLocked: "96 kHz 超では Insert FX は使用不可 — 強制的に OFF。",
    insFxRateLockedAt: (effect: string, maxRate: string): string =>
      `${maxRate} 超では ${effect} は使用不可 — 強制的に OFF。`,
    fx2RateLocked: "FX2 Bus は 96 kHz を超えると使用できません。",
    phantomLockedByHiZ: "先に Hi-Z を切ってください。+48V と Hi-Z は同時にオンにしません",
    hiZLockedByPhantom: "先に +48V を切ってください。+48V と Hi-Z は同時にオンにしません",
    insFxLinkLocked: "Signal Type が STEREO の間は Compander のみ使用可。",
    insFxSlotLocked: "Insert FX はすべて使用中 — 各エフェクトは本体全体で 1 スロットを占有。",
    channelOn: "チャンネル",
    sendOn: "Send",
    toSt: "TO ST",
    hpf: "HPF",
    hpfFreq: "HPF Freq",
    phantom: "+48V",
    phase: "Ф",
    phaseName: "フェーズ",
    clipSafe: "Clip Safe",
    clipSafeEngaged: "Clip Safe がゲインを下げています",
    hiZ: "Hi-Z",
    insertFx: "Insert FX",
    insertFxType: "EFFECT TYPE",
    insertFxOn: "Insert FX ON",
    compEqType: "COMP/EQ Type",
    eqOn: "EQ",
    compOn: "COMP",
    gateOn: "GATE",
    eqOneKnobType: "1-knob Type",
    bandOn: "Band",
    filterType: "Type",
    frequency: "Freq",
    q: "Q",
    eqGain: "Gain",
    fineTag: "FINE",
    fineHint: "微調整 — Shift を押しながら操作",
    fineHintLatch: "微調整 — Shift で切り替え",
    eqBand: { low: "LOW", lowMid: "LOW MID", highMid: "HIGH MID", high: "HIGH" },
    // Rows of the GATE / COMP / DUCKER screens. The unit keeps them in English with its display
    // language set to Japanese, so they are not translated.
    dyn: {
      threshold: "Threshold",
      range: "Range",
      attack: "Attack",
      hold: "Hold",
      decay: "Decay",
      ratio: "Ratio",
      gain: "Gain",
      release: "Release",
      knee: "Knee",
    },
    autoMakeup: "Auto Makeup",
    oneKnob: "1-knob",
    oneKnobLevel: "1-knob Level",
    ssmcs: {
      title: "SSMCS",
      sweetSpotData: "Sweet Spot Data",
      compDrive: "Comp Drive",
      morphing: "Morphing",
      outGain: "Out Gain",
      sideChain: "Side Chain",
      bands: { low: "LOW", mid: "MID", high: "HIGH" },
    },
    fxEffect: {
      title: "FX Effect",
      effectType: "EFFECT TYPE",
      effectOn: "Effect",
      level: "Mix",
      params: {
        reverbTime: "Rev.Time",
        initialDelay: "Ini.Delay",
        decay: "Decay",
        roomSize: "Room Size",
        diffusion: "Diffusion",
        density: "Density",
        hpf: "HPF",
        lpf: "LPF",
        hiRatio: "Hi.Ratio",
        lowRatio: "Lo.Ratio",
        lowFreq: "Lo.Freq.",
        feedback: "FB.Gain",
        erRevDelay: "E/R Delay",
        erRevBalance: "E/R Bal.",
        delayTime: "Delay",
        sync: "Sync",
        bpm: "BPM",
        note: "Note",
      },
    },
    insertFxEffect: {
      title: "Insert Effect",
      bandLow: "Low",
      bandMid: "Mid",
      bandHigh: "High",
      scale: "Scale",
      scaleNotes: "音",
      scaleChromatic: "Chromatic",
      scaleMajor: "Major",
      scaleCustom: "Custom",
      scaleSingle: "Single",
      scaleNaturalMinor: "Natural Minor",
      scaleHarmonicMinor: "Harmonic Minor",
      scaleMelodicMinor: "Melodic Minor",
      scalePentatonic: "Pentatonic",
      midiControlDeviceOnly:
        "Setting 以降は補正が狙う音が本体自身の USB-MIDI ポートから届き、OFF に戻すまで 12 音マスクは本体のものになります。Setting では Chromatic 以外の Scale を、Real Time では Custom と Single を選べ、Real Time のあいだマスクは空のままです。",
      params: {
        bypass: "Bypass",
        threshold: "Threshold",
        ratio: "Ratio",
        attack: "Attack",
        release: "Release",
        outGain: "Out Gain",
        width: "Width",
        gain: "Gain",
        volume: "Volume",
        bass: "Bass",
        middle: "Middle",
        treble: "Treble",
        presence: "Presence",
        output: "Output",
        spType: "SP Type",
        micPosition: "Mic Position",
        gate: "Gate",
        gateLevel: "Gate Level",
        blend: "Blend",
        distortion: "Distortion",
        mod: "Modulation",
        modSpeed: "Speed",
        modDepth: "Depth",
        type: "Type",
        ampType: "Amp Type",
        master: "Master",
        coarse: "Coarse",
        fine: "Fine",
        formant: "Formant",
        correction: "Correction",
        mix: "Mix",
        key: "Key",
        speed: "Speed",
        tolerance: "Tolerance",
        limitLow: "Limit Low",
        limitHigh: "Limit High",
        midiControl: "MIDI Control",
        oneKnobOn: "1-knob",
        oneKnobLevel: "1-knob Level",
        xoverLowMid: "L-M Xover",
        xoverMidHigh: "M-H Xover",
      },
    },
    duckerOn: "Ducker",
    gainAnalog: "A.Gain",
    gainDigital: "D.Gain",
    oscOn: "Oscillator",
    oscLevel: "Level",
    oscMode: "Mode",
    oscWidth: "Width",
    oscInterval: "Interval",
    oscAssignL: "Assign L",
    oscAssignR: "Assign R",
    delayTitle: "DELAY",
    delayOn: "DELAY",
    delayFrameRate: "Frame rate",
    delayTime: "Delay Time",
    monitorOn: "Monitor",
    cueInterrupt: "CUE Interrupt",
    cue: "CUE",
    cueOffline: "CUE はライブ同期中に本体の CUE を切り替えます。",
    mono: "MONO",
    phonesLevel: "PHONES Level",
    on: "ON",
    off: "OFF",
    notReadFromDevice: "デバイスから読み込まれていません — 計画の初期値を表示しています。",
    sideGroup: "表示する側",
    sideL: (ch: string) => `${ch} (L)`,
    sideR: (ch: string) => `${ch} (R)`,
    shareJack: (others: string, jack: string) =>
      `${others} と共有しています。${jack} に載るチャンネルはすべてこの値を使います。`,
    shareSource: (others: string, source: string) =>
      `${others} と共有しています。${source} に載るチャンネルはすべてこの値を使います。`,
    selectionOnly: "選択のみ — Send パラメータはありません。",
    directOutTap:
      "ダイレクト出力 — チャンネルの Rec Point でタップ (フェーダー・Ducker より前段)。含めるには STEREO / MIX Bus 経由で送ってください。",
    sdRecTap:
      "このチャンネルを Rec Point でタップして録音します (既定は pre-fader)。録音する段はチャンネルの Rec Point で選べます。",
    duckerKeyTap:
      "Ducker キー — キー元チャンネルの Rec Point (フェーダー・Ducker より前段) でタップ。そのチャンネルのフェーダー・ミュートはトリガーに影響しません。post-fader でトリガーするには STEREO / MIX Bus をキーにしてください。",
    patchNoMono:
      "MONO を持つのは MONITOR 1 / 2 だけです。この出力を MONITOR Bus からパッチすると、MONO に切り替えられます。",
    patchViaMonitor:
      "この出力は MONITOR Bus の MONO スイッチに従います — 切り替えは MONITOR ノードで行います。CUE Interrupt も同じ経路にあるため、それが ON の間は CUE を押すとこの出力の音が置き換わります。",
    monoPhonesShared:
      "MONO が ON です。この MONITOR の PHONES はその後段でタップされるため、ヘッドホンにも同じ信号が出ます — 片方をステレオで残すには、もう一方の MONITOR を使います。",
    monoUnavailable: "利用不可 — MONITOR 経由",
    monoVia: (state: string, monitor: string): string => `${state} (${monitor} から)`,
    fixedConnection: "固定接続 — 常時有効で削除できません。",
    duckerPreSend:
      "このチャンネルの Ducker は ON ですが、この PRE (Pre-fader send) はその手前でタップするため Ducker が効きません。Ducker を含めるには POST に切り替えてください。",
    busType: "BUS Type",
    panLink: "Pan Link",
    busFixedSend:
      "Send は ON のみ有効 (BUS Type: FIXED)。Send 元のフェーダーの後で固定レベルで取り出し、Send 元自身の PAN / BAL に従う。",
    panLinked: "パンは Send 元自身の PAN / BAL と同じです (Pan Link)。ここで回すと Send 元の位置が動きます。",
    sdRecTrackCount: "Track Count",
    sdRecTrackCountLive: "Track Count はデバイス側でのみ設定できます (broker が公開するのは 8 段階のうち 1 つだけ)。",
    sdRecTrackCountRate: (ceiling: number): string =>
      `このサンプルレートで使えるのは ${ceiling} トラックです。レートを下げ直してもレコーダーは戻りません — 戻す手段はデバイス本体の microSD 画面 → RECORDER メニュー → [Track Count] です。`,
    signalType: "Signal Type",
    panBal: "PAN / BAL",
    deleteConnection: "この接続を削除",
    hideNode: "このノードを隠す",
    notesPlaceholder: "このノードにノートを書き込む… (キャンバスと出力画像に表示されます)",
    recentPlans: "最近使った計画",
    none: "-",
    connKind: {
      source: "入力ソース選択 (1系統)",
      patch: "出力パッチ (1系統)",
      key: "Ducker キーソース (1系統)",
      send: "Bus send (加算)",
      sendSwitch: "Bus send (ON/OFF スイッチ)",
      record: "SD Rec ソース選択 (単一)",
    },
    connKindMonoPair: "出力パッチ (1系統または MONO IN ペア)",
    nodeKind: {
      input: "入力ソース",
      channel: "ミキサーチャンネル",
      bus: "Bus",
      output: "出力",
      ducker: "Ducker",
    },
    legend: {
      signals: "接続の種類",
      nodes: "ノード",
      source: "ソース選択",
      send: "Bus send (TO ST・OSC はオン・オフのみ)",
      patch: "出力・録音の選択",
      pre: "Pre-fader send",
      recPoint: "Rec Point タップ (チャンネルのみ)",
    },
  },
  // A lane's caption (which end of the processor it reads) and the display mode names stay
  // English in ja too (they name the same points as the CONSOLE's meter-point badges, so the
  // wording matches). The heading above them is not covered by this: the Japanese user guide
  // writes it in katakana, so it is translated as 「メーター」.
  // A level tap's name is not here — the tap table in `core/meters.ts` carries it.
  dynTuning: {
    close: "閉じる",
    display: "Display",
    parameters: "パラメーター",
    laneIn: "Input",
    laneOut: "Output",
    readouts: "メーター",
    peakPrefix: "pk",
    noReading: "—",
    driven: "本体が制御中",
    bypassed: "バイパス中 — 値はここで保持・編集できますが、設定した内容は信号に届きません。",
    gate: {
      title: "Gate",
      open: "Gate 画面",
      tapGr: "GATE GR",
      curveHint: "曲線のニーを左右にドラッグして Threshold を設定します。",
    },
    comp: {
      title: "Comp",
      open: "Comp 画面",
      tapGr: "COMP GR",
      curveHint:
        "曲線はスライダーが信号に与える変化、点はライブのレベルです。近づけるには Attack と Release を調整します。",
    },
    eq: {
      title: "EQ",
      open: "EQ 画面",
      unusedByType: "このTypeでは無効",
      fixedBand: "このBandでは固定",
      oneKnobDrives: "4 バンドは 1-knob のレベルからデバイスが計算します。カーブが追従するのはライブ同期中のみです。",
      plotHint: "曲線は EQ の周波数特性、各マーカーは各バンドの周波数を示します。",
    },
    ssmcs: {
      open: "SSMCS 画面",
      faceMain: "Main",
      faceComp: "Comp",
      faceEq: "EQ",
      mainHint:
        "Comp Drive はコンプの曲線を動かします。Sweet Spot Data と Morphing は両方を、ライブ同期中のみ動かします。",
      scHint:
        "Side Chain が変えるのはコンプの反応する対象で、音そのものではありません。曲線はそれが生むリダクション量なので、下がっている帯域ほどコンプが強くかかります。",
    },
    ducker: {
      title: "Ducker",
      open: "Ducker 画面",
      tapKey: (label: string): string => `KEY · ${label}`,
      noKey: "KEY · なし",
      tapGr: "DUCKER GR",
      hint: "斜線は変化にかかる時間で、変化の形ではありません。Key はステレオでも 1 本 (L+R の和) です。",
    },
    fx: {
      title: "FX EFFECT",
      open: "FX Effect 画面",
    },
    insfx: {
      title: "INS FX",
      open: "Insert FX 画面",
      tapGr: "INS FX GR",
      faceMain: "Main",
      vibOnly: "Vib のみ",
      curveHint:
        "ウィンドウより下ではさらに押し下げ、スレッショルドより上では抑え、0 dB を超えると頭打ちになります。ドットは実測レベルです。",
      mbcMainHint:
        "クロスオーバーがスペクトルをどこで分割し、各バンドがどのレベルで戻されるかです。段差は 2 つの設定値であり、本体のフィルタースロープではありません。",
      mbcBandHint:
        "このバンドだけを示します。自身のスレッショルドより上を抑え、メイクアップを加えます。リダクションはこのバンドについて本体が報告する値です。",
      mbcBandBypassed:
        "このバンドはバイパスされています。圧縮もメイクアップも掛からず unity で素通しします。下の値は保持され、ここで編集もできます。",
      mbcOneKnob:
        "1-knob が ON です。バンドの値・Release・クロスオーバーはレベルから、Out Gain は ON 時に本体が設定し、編集は送られません。1-knob は送られます。",
      deviceOnlyTag: "本体で設定",
    },
  },
  sim: {
    lockUnitOnly: "本体で変更してください",
    lockUnitOp: "本体で操作してください",
    lockRateLinked: "デバイスとの接続中は変更できません",
    lockRateScope:
      "範囲が「シーンのみ」のライブ同期ではサンプルレートを扱いません。ライブ同期中に変えるには、環境設定 > デバイス入出力 > 範囲 を「全対応項目」にしてください",
    lockRateFollowUsb: "ライブ同期中に変えるには Follow USB をオフにしてください",
    lockIntegrationOff: "ライブ同期中、Cubase・Nuendo・MixKey が本体とつながっている間に使えます",
    lockSetupOffline: "ライブ同期中に使えます",
  },
  shelf: {
    title: "非表示",
    showAll: "全て表示",
    restore: (label: string): string => `${label} を表示`,
  },
  selbar: {
    title: " 件選択中",
    hide: (n: number): string => `${n} 件を非表示`,
    clear: "選択解除",
  },
  tooltip: {
    addNote: "ノートを追加",
    collapseNote: "ノートを最小化",
    expandNote: "ノートを展開",
    recPointTap: "Rec Point タップ — フェーダー・Ducker より前段。USB 出力・microSD Rec へはここからドラッグします",
  },
  warning: {
    title: "サンプルレート注意",
    insFx: "96 kHz 超では Insert FX (MONO IN ch・MIX・STEREO) を使用できません。",
    stereoEq: "176.4 / 192 kHz ではステレオ ch (CH 5/6–11/12) の EQ を使用できません。",
    fx2: "96 kHz 超では FX2 Bus を使用できません。",
    duckerTitle: "Ducker が USB ダイレクト出力に乗りません",
    duckerBypass: (label: string): string =>
      `${label} — Ducker ON ですが USB ダイレクト出力はその前段でタップします。含めるには STEREO / MIX Bus 経由で送ってください。`,
  },
  status: {
    loaded: (model: string): string => `${model} を読み込みました — ドラッグで配置・結線`,
    switchedModel: (model: string): string => `${model} に切り替えました`,
    sampleRate: (rate: string): string => `サンプルレート: ${rate}`,
    insertsOffAtRate: (rate: string, list: string): string =>
      `サンプルレート: ${rate} — ${list} を外しました。デバイスはエフェクトごとの上限を超えたレートでインサートを外します。レートを下げても元には戻りません。`,
    followUsbOn: "Follow USB ON — デバイスは USB ホストのクロックで動作します",
    followUsbOff: "Follow USB OFF — デバイスは自身のクロックで動作します",
    newPlan: "新規計画を作成しました",
    paramsBounded: (count: number): string =>
      `このアプリが書き込める範囲の外にあった保存値 ${count} 件を、送信できる最も近い値に寄せました`,
    paramsDropped: (count: number): string =>
      `このアプリが書き込める値ではなかった保存値 ${count} 件を削除し、既定値を使います`,
    streamingSourceSupplied: "計画に STREAMING のソースが無かったため、STREAMING を STEREO にしました",
    textsRewritten: (count: number): string =>
      `名前・メモ ${count} 件を、本体の名前画面と画像出力が扱える形に書き換えました`,
    colorsDropped: (count: number): string => `本体に無いノードの色 ${count} 件を削除し、既定の色を使います`,
    linkedPairsAligned: (count: number): string =>
      `STEREO リンクしたペア ${count} 組で、共有する設定を本体と同じく奇数チャンネルの値にそろえました`,
    sendLevelsSupplied: (count: number): string =>
      `レベルの書かれていなかった Send ${count} 件を、書き込みが送る 0 dB にしました`,
    booleanParamsConverted: (count: number): string =>
      `数値で書かれていたオン/オフの値 ${count} 件を、オン/オフの値に変換しました`,
    linkedSendPansAligned: (count: number): string =>
      `Pan Link がオンの MIX への SEND PAN ${count} 件を、Send 元自身の PAN / BAL にそろえました`,
    mbcOneKnobAligned:
      "M.B.Comp の 1-knob がオンのため、Attack・Bypass・Release・クロスオーバー・Out Gain を本体と同じ値にそろえました",
    compOneKnobsAligned: (count: number): string =>
      `COMP の 1-knob がオンのチャンネル ${count} 件で、Attack・Release・Auto Makeup を本体と同じ値にそろえました`,
    streamingSourceUnlisted: "本体の STREAMING が一覧に無い状態だったため、計画では STEREO にしました",
    planLoaded: "計画を読み込みました",
    phantomHiZBothOn: (channels: string): string => `${channels} で +48V と Hi-Z が両方オンになっています`,
    phantomRefusedByRead: (channels: string): string =>
      `${channels} の +48V はオンにしませんでした — 本体で Hi-Z がオンになっています。+48V と Hi-Z は同時にオンにしません`,
    hiZRefusedByRead: (channels: string): string =>
      `${channels} の Hi-Z はオンにしませんでした — 本体で +48V がオンになっています。+48V と Hi-Z は同時にオンにしません`,
    recentRemoved: (name: string): string => `${name} を最近使った計画から削除しました`,
    planSaved: "計画を保存しました",
    savedTo: (name: string): string => `${name} に保存しました`,
    openedFrom: (name: string): string => `${name} を開きました`,
    shareUrlCopied: "共有 URL をクリップボードにコピーしました",
    shareUrlInBar: "クリップボードを使用できません — アドレスバーから共有 URL をコピーしてください",
    shareUrlError: (message: string): string => `共有 URL の生成に失敗しました: ${message}`,
    planDownloaded: "計画の JSON をダウンロードしました",
    canceled: "キャンセルしました",
    pngExported: "PNG を出力しました",
    pdfExported: "PDF を出力しました",
    arranged: "既定レイアウトに整列しました",
    busyDeviceRead: "本体から読み取り中です — 完了してからもう一度お試しください",
    busyFileFlow: "ファイルを開くか保存している最中です — 完了してからもう一度お試しください",
    simUnitAction: "この操作は本体自身のもので計画には含まれないため、SIM では行いません",
    busySwitchRead: "この計画はデバイスの機種の計画に差し替え中です — 読み取りが終わってからもう一度お試しください",
    deviceLinkBusy: "別のデバイス操作が接続を掴んでいます — 完了してからもう一度お試しください",
    fetchConnecting: "デバイスに接続しています…",
    fetchedDevice: (model: string, n: number): string => `${model} から ${n} 件の設定を取得しました`,
    fetchedUnread: (model: string, n: number, unread: number): string =>
      `${model} から ${n} 件取得、${unread} ノード未取得`,
    fetchPartial: (n: number, failed: number, unread: number): string =>
      `${n} 件取得、${failed} 件失敗` + (unread ? `、${unread} ノード未取得` : ""),
    fetchSwitchIncomplete: (failed: number, device: string, ui: string): string =>
      `読み取りが不完全だった (${failed} 件失敗) ため、${device} へは切り替えず、計画は ${ui} のままです。`,
    settingsImported: (name: string, n: number): string => `${name} から ${n} 件の設定を取り込みました`,
    settingsPartial: (n: number, failed: number, unread: number): string =>
      `${n} 件取り込み、${failed} 件失敗` + (unread ? `、${unread} ノード未取得` : ""),
    settingsError: (message: string): string => `設定ファイルを取り込めませんでした: ${message}`,
    settingsRateKept: (rate: string): string =>
      ` — デバイスの範囲が「シーンのみ」のため、サンプリング周波数は ${rate} のままです`,
    dropUnsupported: (name: string): string => `${name} は開けません — 計画ファイル (.json) をドロップしてください`,
    dropUnsupportedSettings: (name: string): string =>
      `${name} は開けません — 計画ファイル (.json) か設定ファイル (.urxf) をドロップしてください`,
    dropMultiple: "ファイルは 1 つずつドロップしてください",
    fetchError: (message: string): string => `デバイスからの取得に失敗しました: ${message}`,
    compareConnecting: "デバイスと照合しています…",
    compareMatch: (compared: number, ms: number): string => `読み取った全 ${compared} 件がデバイスと一致 (${ms} ms)`,
    compareDiff: (differ: number, compared: number, ms: number): string =>
      `読み取った ${compared} 件中 ${differ} 件がデバイスと相違 (${ms} ms)`,
    comparePartial: (differ: number, compared: number, failed: number, ms: number): string =>
      `${compared} 件中 ${differ} 件が相違、${failed} 件は読み取れず (${ms} ms)`,
    compareError: (message: string): string => `デバイスとの照合に失敗しました: ${message}`,
    writePhantomHiZ: (channels: string): string =>
      `${channels} で +48V と Hi-Z が両方オンになっています — どちらかを切ってから書き込んでください。何も送信していません`,
    writeConnecting: "デバイスに接続しています…",
    writeNoChanges: "デバイスは計画と一致しています — 書き込む変更はありません",
    writeNamesNotSent: (strips: string): string =>
      `書き込む変更はありません — ${strips} の名前は空のため送信せず、デバイスは自身の名前を保ちます`,
    written: (n: number): string => `${n} 件の設定をデバイスに書き込みました`,
    writePartial: (n: number, failed: number): string => `${n} 件書き込み、${failed} 件失敗`,
    writeStopped: (n: number, notSent: number): string => `失敗のため書き込みを停止: ${n} 件送信、${notSent} 件未送信`,
    writeResidual: (n: number): string => `書き込みましたが ${n} 件が反映されませんでした (コンソール参照)`,
    writeUnconfirmed: (n: number, unread: number): string =>
      `${n} 件の設定を書き込みましたが、${unread} 件を読み戻せず、デバイスの状態を確認できていません`,
    writeReadFailed: (n: number): string => `書き込みを中止しました: ${n} 件の設定をデバイスから読み取れません`,
    writeError: (message: string): string => `デバイスへの書き込みに失敗しました: ${message}`,
    deviceSetupReading: "本体の設定を読み込んでいます…",
    deviceSetupRead: "本体の設定を読み込みました",
    deviceSetupApplying: "本体へ設定を適用しています…",
    deviceSetupApplied: (n: number): string => `${n} 件の設定を本体へ適用しました`,
    selfTestRunning: "デバイスのセルフテストを実行中… 切断しないでください (メニューから中止できます)",
    selfTestRefused:
      "セルフテストを開始しませんでした — 復元に必要なパラメーターの一部を事前に読み取れませんでした。デバイスには何も書き込んでいません。",
    selfTestRefusedHead:
      "セルフテストを開始しませんでした — 書き込むと他の設定も変わる設定をデバイスから読み取れませんでした。デバイスには何も書き込んでいません。",
    selfTestModelMismatch: (device: string, model: string): string =>
      `セルフテストを開始しませんでした — 接続中のデバイスは ${device} で、${model} ではありません。デバイスには触れていません。`,
    selfTestCancelled: "セルフテストを中止しました — デバイスは無音状態です。元に戻すには再度取得してください",
    selfTestCancelledUntouched: "セルフテストを書き込み前に中止しました — デバイスには触れていません",
    selfTestPass: (n: number): string => `セルフテスト合格: ${n} 件のパラメータを書き込み、同一に読み戻しました`,
    selfTestFail: (n: number): string => `セルフテスト失敗: 書き込み後に ${n} 件が一致しませんでした`,
    selfTestIncomplete: (n: number): string =>
      `セルフテスト中断: 停止した時点で ${n} 件が差分のままです — レポートを確認してください`,
    selfTestRestoreFail: "セルフテスト: デバイスが復元されていない可能性があります — 再度取得して確認してください",
    selfTestUnverified: (confirmed: number, roundTripped: number, refuted: number, untestable: number): string =>
      `セルフテストの推測: 確認 ${confirmed} 件・往復のみ ${roundTripped} 件・否定 ${refuted} 件・検証不能 ${untestable} 件`,
    selfTestError: (message: string): string => `セルフテストのエラー: ${message}`,
    liveConnecting: "ライブ同期のため接続中…",
    liveOn: (model: string, n: number): string => `ライブ同期 オン · ${model} · ${n} 件読込`,
    liveOff: "ライブ同期 オフ",
    liveRescoped: "デバイスの範囲が変わったため、新しい範囲でライブ同期を始め直します",
    liveSynced: (n: number): string => `→ デバイス (${n})`,
    liveFollowing: "← デバイス…",
    liveFollowed: (n: number): string => `← デバイス (${n})`,
    liveHeld: (read: number, unrunnable: number, source: number): string =>
      [
        `← デバイス (${read})`,
        ...(unrunnable ? [`${unrunnable} 件を保持して再送 (実機のサンプルレートでは動かせない設定)`] : []),
        ...(source ? [`${source} 件を保持して再送 (読み取り中にここで描いたソース)`] : []),
      ].join(" · "),
    sharedSetting: (dropped: string, kept: string, more: number): string =>
      `${dropped} は ${kept} と本体上の設定を共有しています${more > 0 ? ` (他 ${more} 件)` : ""} — 本体に届くのは ${kept} の値だけです`,
    liveError: (message: string): string => `ライブ同期を停止: ${message}`,
    unverifiedGuess: (key: string): string => `このアドレスは未検証のマッピング ${key} に基づく`,
    linkLogFailed: (message: string): string => `リンク台帳を書き出せませんでした: ${message}`,
    connected: "接続しました",
    connectionDeleted: "接続を削除しました",
    fixedConnection: "固定接続は削除できません",
    streamingSourceRequired:
      "STREAMING は本体と同じく常に 1 つのソースを持ちます — 差し替えるには別のソースを STREAMING へ接続してください",
    noteMinimized: "ノートを最小化しました",
    noteExpanded: "ノートを展開しました",
    hidUnused: (n: number): string => `未接続の ${n} ノードを非表示にしました`,
    noneToHide: "非表示にできる未接続ノードはありません",
    hidNode: (label: string): string => `${label} を非表示にしました`,
    hidSelected: (n: number): string => `${n} 件を非表示にしました`,
    shownNode: (label: string): string => `${label} を表示しました`,
    shownAll: "全ノードを表示しました",
    pathTraced: (label: string, n: number): string => `${label} への信号経路を表示中 — ${n} ノード`,
    pathNone: (label: string): string => `${label} に流れる信号経路はありません`,
    undone: "元に戻しました",
    undoneNode: (label: string): string => `${label} の変更を元に戻しました`,
    redone: "やり直しました",
    redoneNode: (label: string): string => `${label} の変更をやり直しました`,
    nothingToUndo: "元に戻せる操作はありません",
    nothingToRedo: "やり直せる操作はありません",
    undoBusyDrag: "ドラッグを終えてから元に戻してください",
    undoDeviceBusy: "本体との通信中です — 完了するまで元に戻せません",
    undoModal: "開いているダイアログを閉じてから元に戻してください",
    undoRateLocked: "デバイス操作の実行中はサンプルレートを元に戻せません",
    undoRateLockedMixed:
      "この操作はサンプルレートの変更を含みます — デバイス操作の実行中はサンプルレートを元に戻せないため、操作全体を保留しました。破棄はしていません。その操作が終われば同じ操作が通ります",
    undoRateScope:
      "範囲が「シーンのみ」のライブ同期ではサンプルレートを扱いません。ライブ同期中に元に戻すには、環境設定 > デバイス入出力 > 範囲 を「全対応項目」にしてください",
    undoRateScopeMixed:
      "この操作はサンプルレートの変更を含みます — 範囲が「シーンのみ」のライブ同期ではサンプルレートを扱わないため、操作全体を保留しました。破棄はしていません。環境設定 > デバイス入出力 > 範囲 を「全対応項目」にすれば同じ操作が通ります",
    undoRateFollowUsb: "ライブ同期中にサンプルレートを元に戻すには Follow USB をオフにしてください",
    undoRateFollowUsbMixed:
      "この操作はサンプルレートの変更を含みます — ライブ同期は Follow USB がオフの間しかサンプルレートを受け付けないため、操作全体を保留しました。破棄はしていません。Follow USB をオフにすれば同じ操作が通ります",
    undoPhantomHiZ: (channels: string): string =>
      `この操作は ${channels} で +48V と Hi-Z を両方オンにします — どちらかを切ってからやり直してください。操作は保留しました。破棄はしていません`,
    undoWireShape: (receivers: string): string =>
      `この操作は ${receivers} を本体が持てない結線にします — 1 本しか受けない入力に 2 本、または STREAMING のソースが無い状態です。操作は保留しました。破棄はしていません`,
    midiBusy: "本体との通信中またはファイル操作中です — 完了するまで MIDI 入力は無視されます",
    themeDark: "ダークモードに切り替えました",
    themeLight: "ライトモードに切り替えました",
    themeAuto: "システム設定に追従します",
    language: (name: string): string => `言語: ${name}`,
    loadError: (message: string): string => `読込エラー: ${message}`,
    saveError: (message: string): string => `保存エラー: ${message}`,
    exportError: (message: string): string => `出力エラー: ${message}`,
    updateDownloading: "更新をダウンロード中… 完了後にアプリを再起動します",
    updateInstallFailed: (message: string): string => `更新をダウンロード・インストールできませんでした: ${message}`,
    updateRestartFailed: (message: string): string =>
      `更新はインストールしましたが、アプリを再起動できませんでした (${message})。新しいバージョンを使うにはアプリを終了して開き直してください。`,
  },
  confirm: {
    discard: "保存していない変更があります。破棄してよろしいですか?",
    deviceSetupDiscard: "本体設定に適用していない変更があります。破棄してよろしいですか?",
    update: (version: string): string => `バージョン ${version} が利用可能です。今すぐ更新しますか?`,
    switchModel: (device: string, ui: string): string =>
      `接続中のデバイスは ${device} ですが、${ui} を選択中です。${device} に切り替えて (現在の計画は破棄) 取得しますか?`,
    write: (n: number): string => `${n} 件の変更をデバイスに書き込みますか? デバイスの現在の設定を上書きします。`,
    unauthoredWrite: (strips: string): string =>
      `操作していない設定も変更されます — プランが補った既定値、または読み込み後にデバイス側で変わった値です。\n対象: ${strips}`,
    namesNotSent: (strips: string): string =>
      `${strips} の名前は空のため送信しません — デバイスは自身の名前を保ちます。`,
    firmwareMismatch: (device: string, supported: string): string =>
      `接続中のデバイスのファームウェア (${device}) は、このアプリの動作確認バージョン (${supported}) と異なります。正しく動作しない可能性があります。続行しますか?`,
    selfTest:
      "デバイスのセルフテストを実行しますか? 全パラメータを一時的に上書きして書き込みを検証し、元の状態に復元します。実行中は出力をミュート (フェーダー最小・発振器/ファンタム OFF) します。",
    selfTestExport:
      "この機種には未確認のパラメータ対応があります。確認のため返送できるよう、セルフテストのレポートを保存しますか?",
    selfTestFailExport:
      "セルフテストで問題が見つかりました。一致しなかったパラメータと、読み取り/書き込みに失敗した項目を記載したレポートを保存しますか?",
    deviceErrorExport: "一部のパラメータを読み取り/書き込みできませんでした。各失敗を記載したレポートを保存しますか?",
    deviceUnappliedExport:
      "一部の実機の値は適用されませんでした (読み取り中にこちらで編集されたためです)。一覧を記載したレポートを保存しますか?",
    importSettings: (name: string, model: string): string =>
      `${name} を現在の ${model} 計画に取り込みますか? 設定ファイルにはどの機種で保存したかの情報が無いため、${model} で合っているか確認してください。配置・非表示・ノートは維持されます (ファイルに編集状態は含まれません)。`,
    importSettingsLive: (name: string, model: string): string =>
      `${name} を現在の ${model} 計画に取り込み、本体に書き込みますか? ライブ同期中のため、取り込んだ設定はすべて本体に送られます (サンプリング周波数は今のままです)。設定ファイルにはどの機種で保存したかの情報が無いため、${model} で合っているか確認してください。配置・非表示・ノートは維持されます。`,
    reclock: (deviceRate: string, planRate: string): string =>
      `デバイスは ${deviceRate}、計画は ${planRate} です。書き込むとデバイスがリクロックし USB ストリームが再ネゴシエートされるため、音声が一瞬中断されます。コンピューター側はデバイスの新しいレートに追従します。続行しますか?`,
    followUsbOn:
      "Follow USB を ON にしますか? デバイスはクロックをコンピューターに委ねます。コンピューターが別のレートで動作している場合は直ちにそのレートへリクロックするため、音声が中断される場合があります。レートが既に一致していれば何も起きません。",
    followUsbOffImport: (rate: string): string =>
      `設定ファイルは Follow USB を OFF にします。OFF にしますか? デバイスはクロックをコンピューターから受け取るのをやめ、${rate} で動作します。`,
    insertsOffAtRate: (list: string): string =>
      `このレートでは ${list} が外れます。デバイスはエフェクトごとの上限を超えたレートではインサートを動かさず、そのチャンネルやバスを No Effect にします。レートを下げても元には戻りません。`,
    trackCountDrop: (from: number, to: number): string =>
      `microSD レコーダーの Track Count が ${from} トラックに設定されており、このレートで使えるのは ${to} トラックです。デバイスが自動的に下げます。このアプリから書ける値では元に戻せません — 戻す手段はデバイス本体の microSD 画面 → RECORDER メニュー → [Track Count] です。`,
    trackCountMayDrop:
      "48 kHz より上ではデバイスが microSD レコーダーの Track Count もそのレートで使える本数まで下げます。このアプリから書ける値では元に戻せません — 戻す手段はデバイス本体の microSD 画面 → RECORDER メニュー → [Track Count] です。",
    writeRetry: (sent: number, notSent: number): string =>
      `失敗により書き込みが停止しました: ${sent} 件がデバイスに届き、${notSent} 件が未送信です。もう一度実施しますか? 差分のある設定のみを送信します。`,
    writeRetryUnread: (sent: number, unread: number): string =>
      `${sent} 件がデバイスに届きましたが、${unread} 件を読み戻せず結果を確認できていません。もう一度実施しますか? 差分のある設定のみを送信します。`,
  },
  consent: {
    title: "ご利用の前に",
    body: [
      "URX Router は接続中の YAMAHA URX シリーズインターフェースに設定を書き込み、その時点のミキサー設定を上書きできます。制御プロトコルは公式ドキュメントではなく独立した解析で判明したものであり、ハードウェアへのデータ送信には常に一定のリスクが伴います。",
      "パラメータの対応は URX44V と URX22 で実機検証済みです。URX44 は未検証です。",
      "本ソフトウェアは「現状のまま」提供され、いかなる保証も伴いません。作者は、その使用により生じたハードウェアの損傷・設定の喪失その他の損害について一切の責任を負いません。",
    ],
    accept: "続行することで、このリスクを受諾したものとします。",
    agree: "同意して続行",
    quit: "終了",
  },
  rateChoice: {
    title: "デバイスが USB ホストに追従しています",
    intro: (planRate: string, deviceRate: string): string =>
      `計画は ${planRate} ですが、デバイスは ${deviceRate} で動作しており、Follow USB が ON のためクロックをコンピューターから受け取っています。ここで ${planRate} を書き込むとデバイスは一度リクロックしますが、まもなく ${deviceRate} に戻ります。`,
    hiRateNote: (limits: string): string =>
      `${limits} これらの設定は今回の書き込みから除外されますが、計画には保持されるため、レートを下げれば再び書き込まれます。`,
    trackCountDrop: (from: number, to: number): string =>
      `Follow USB を OFF にしてプランのレートで書き込むと、microSD レコーダーの Track Count が下がります: 現在 ${from} トラックで、そのレートで使えるのは ${to} トラックです。このアプリから書ける値では元に戻せません — 戻す手段はデバイス本体の microSD 画面 → RECORDER メニュー → [Track Count] です。`,
    insertsOffAtRate: (deviceRate: string, list: string): string =>
      `${deviceRate} で書き込むと、デバイスと同じく計画から ${list} が外れます。それらのエフェクトはそのレートでは動きません。`,
    adopt: (deviceRate: string): string => `${deviceRate} で書き込む`,
    release: (planRate: string): string => `Follow USB を OFF にして ${planRate} で書き込む`,
    cancel: "キャンセル",
  },
  loadReport: {
    title: "計画を読み込めませんでした",
    intro:
      "このリンクの計画に問題があり、読み込みを中止しました。下のレポートをコピーし、計画を生成したツールに渡して修正してから、再度読み込んでください。",
    copy: "コピー",
    copied: "コピーしました",
    close: "閉じる",
    slotTitle: "インサート FX スロットが競合しています",
    slotIntro:
      "この計画では 2 つのノードが本体で共有される同じインサート FX スロットを取り合っています。アプリの画面からはこの状態を作れませんが、実機からの読み取りではこの検査を行わないため、実機から保存した計画には含まれ得ます。そのため中止ではなく警告としています。読み込んでこのアプリで解消するか、閉じて計画の生成元で修正してください。",
    loadAnyway: "このまま読み込む",
  },
  compareReport: {
    title: "デバイスとの照合",
    intro:
      "読み取り専用 — 書き込みは行っていません。ツールが往復する全 param をデバイスから読み、計画と照合しました。要約に件数を、全件ログに 1 件ずつ示すので、一致を鵜呑みにせず検証できます。取り込んだ設定ファイルを実機と照合する用途に使えます — ファイルの元となった個体を接続して照合してください。",
  },
  cueBar: {
    label: (names: string): string => `CUE ${names}`,
    clear: "Clear CUE",
  },
  linkStats: {
    title: "Device Center リンク",
    row: {
      up: "接続経過",
      sent: "送信",
      subscriptions: "購読",
      frames: "購読フレーム",
      reads: "全体読み",
      noanswer: "無応答",
      log: "ログ",
    },
    set: "書込",
    get: "読出",
    params: "param",
    meters: "meter",
    regist: "regist",
    unregist: "unregist",
    stall: (n: number, limit: number): string => `連続 ${n}/${limit} で切断`,
    noLog: "未書き出し",
    copy: "コピー",
    copied: "リンク台帳をクリップボードにコピーしました",
    copyFailed: "クリップボードにコピーできませんでした — パネルから読み取ってください",
  },
  errorBox: {
    title: "エラー",
    close: "閉じる",
  },
  licenses: {
    title: "サードパーティライセンス",
    close: "閉じる",
    error: (message: string): string => `ライセンス情報を読み込めませんでした: ${message}`,
    familyMeta: (components: number, texts: number): string => `${components} コンポーネント · 本文 ${texts} 種`,
  },
  prefs: {
    title: "環境設定",
    close: "閉じる",
    desktopOnly: "デスクトップ版のみ",
    uiSection: "言語とテーマ",
    language: "言語",
    theme: "テーマ",
    themeAuto: "自動",
    themeDark: "ダーク",
    themeLight: "ライト",
    uiNote: "「自動」は OS の外観設定に従います。",
    deviceSection: "デバイス入出力",
    scope: "範囲",
    scopeAll: "全対応項目",
    scopeScene: "シーンのみ",
    deviceNote:
      "デバイスから取得・デバイスへ書き込み・ライブ同期に共通で適用されます。ライブ同期中に変えると、セッションを張り直します。",
    sceneNote:
      "シーンのみでは URX のデバイス全体設定 (モニター・ヘッドホン・出力パッチ・ストリーミング・オシレーター・サンプルレート) に触れません。",
    diagNote: "照合とセルフテストは常に全項目を対象とします。",
    planSection: "計画ファイル",
    saveScope: "保存範囲",
    planFull: "計画全体",
    planNote: "シーン範囲の計画を開いたとき、シーン外の設定は現在の値を維持します。",
    planNoteShare: "共有 URL と JSON ダウンロードに適用されます。",
    versionSection: "アプリケーションのバージョン",
    updateLaunch: "起動時に確認",
    updateNow: "今すぐ確認",
    checking: "確認しています…",
    upToDate: "最新バージョンです。",
    updateAvailable: (version: string): string => `バージョン ${version} が利用可能です。`,
    updateCheckFailed: "更新の確認に失敗しました。",
    warnSection: "警告",
    warnFirmware: "未検証ファームウェア",
    warnRate: "サンプルレート制限",
    warnDucker: "Ducker バイパス",
    warnNote: "OFF にすると該当する警告を表示しません。デバイスの動作には影響しません。",
    controlsSection: "操作",
    wheel: "ホイールステップ",
    wheelOption: (steps: number): string => `1 ノッチ ${steps} ステップ`,
    fine: "ファインチューニング",
    fineHold: "Shift 長押し",
    fineLatch: "ラッチ",
    controlsNote: "ラッチでは Shift を押すたびにファインチューニングが切り替わります。",
    sleepSection: "コンピューターのスリープ",
    preventSleep: "ライブ同期中は抑止",
    sleepNote:
      "ON にすると、ライブ同期の実行中だけコンピューターとディスプレイがスリープしません。自動ロックも同時に抑止されます。同期していない間は通常どおりスリープ・ロックします。",
    sleepFailed: (message: string): string => `スリープ設定を変更できませんでした: ${message}`,
    filesSection: "ファイルと出力",
    exportScale: "出力スケール",
    exportNote: "PNG / PDF の解像度 — グラフの等倍サイズに対する倍率です (ズームには依存しません)。",
    exportBg: "出力背景",
    exportBgActive: "現在のテーマ",
    exportBgDark: "ダーク",
    exportBgLight: "ライト",
    recent: "最近使った計画",
    clearRecent: "一覧をクリア",
  },
  dropzone: {
    plan: "計画ファイル (.json) をドロップして開く",
    planOrSettings: "計画ファイル (.json) または URX 設定ファイル (.urxf) をドロップ",
  },
  speech: {
    slash: "スラッシュ",
    dot: "てん",
  },
  filter: {
    plan: "URX Router 計画",
    settings: "URX 設定ファイル",
    png: "PNG 画像",
    pdf: "PDF 文書",
    report: "セルフテストのレポート",
    errorReport: "デバイスエラーレポート",
  },
  error: {
    trackCountReread: (message: string): string =>
      `サンプルレートの書き込みを送信しましたが、その後 microSD レコーダーの Track Count を読み戻せませんでした: ${message}。レートが運べない本数は実機が自分で下げるため、画面の表示は変更前の値である可能性があります。実機を読み取って確認してください。`,
    deviceSetupRead: (message: string): string => `本体の設定を読み込めませんでした: ${message}`,
    followUsbRead: (message: string): string => `本体の Follow USB の設定を読み込めませんでした: ${message}`,
    deviceSetupWrite: (message: string): string => `設定を適用できませんでした: ${message}`,
    noRule: "この経路は接続できません",
    duplicate: "すでに接続済みです",
    singleInput: "この入力は1系統のみです (既存の接続を外してください)",
    monoPairOnly: "USB 出力に入るのは 1 系統か、MONO IN ペアの 2 チャンネルだけです (先に接続を外してください)",
    cannotConnect: "接続できません",
    noReason: "失敗の原因が報告されませんでした",
    recPointRequired: "USB 出力・microSD Rec はチャンネル上辺の Rec Point タップから配線してください",
    recPointTargets:
      "Rec Point タップの接続先は USB 出力・microSD Rec のみです。他はチャンネルの出力から配線してください",
    shell: {
      brokerUnreachable: "Device Center が起動していません。起動して URX を接続してから再度お試しください。",
      noDevice: "Device Center は起動していますが、URX が接続されていません。本体を接続してから再度お試しください。",
      multipleDevices:
        "URX が複数接続されていて、選択中の Model では接続先を 1 台に決められません。複数台の接続には対応していません。URX を 1 台だけ接続してから再度お試しください。",
      controlWorkerGone:
        "デバイス制御の接続が中断されました。再接続してください。繰り返す場合はアプリを再起動してください。",
      deviceLost: "デバイスとの接続が切断されました (USB 抜去または Device Center 終了)",
      brokerClosed: "Device Center が制御接続を閉じました。起動し直してから再度お試しください。",
      brokerNoVdpPort: (detail: string): string =>
        `Device Center は応答しましたが制御ポートを提示しませんでした (${detail})。Device Center を起動し直してから再度お試しください。`,
      notConnected: "デバイスに接続していません",
      brokerTimeout: (detail: string): string => `Device Center から応答がありませんでした (${detail})`,
      brokerUnresponsive:
        "Device Center が応答しなくなったため、処理を中止しました。Device Center を起動し直してから再度お試しください。",
      brokerRejected: (detail: string): string => `デバイスが書き込みを拒否しました (${detail})`,
      brokerBadResponse: (detail: string): string => `Device Center から想定外の応答が返りました (${detail})`,
      brokerIo: (detail: string): string => `Device Center との通信に失敗しました (${detail})`,
      fileNotFound: "そのパスにファイルがありません",
      fileDenied: "ファイルへのアクセスが拒否されました",
      fileIo: (detail: string): string => `ファイルの読み書きに失敗しました (${detail})`,
      fileBadExtension: (detail: string): string => `対応していない拡張子です (この操作で扱えるのは ${detail})`,
      fileNoTemp: "一時ファイルの名前がすべて使われているため、ファイルの隣に一時ファイルを作れませんでした",
      pngEncode: "画像を PNG に変換できませんでした",
      canvasUnavailable: "描画キャンバスを利用できないため画像を生成できませんでした",
      svgRasterize: "盤面を画像として描画できませんでした",
      convergeFailed: "デバイスへの書き込みが理由を示さずに失敗しました",
      midiPortNotFound: "その MIDI ポートは使用できません。デバイスを接続し直して選び直してください。",
      midiOutputNotOpen: "MIDI 出力ポートが開いていません",
      midiInitFailed: (detail: string): string => `MIDI サブシステムを開始できませんでした (${detail})`,
      midiOpenFailed: (detail: string): string => `MIDI ポートを開けませんでした (${detail})`,
      midiSendFailed: (detail: string): string => `MIDI メッセージを送信できませんでした (${detail})`,
      keepAwakeFailed: (detail: string): string => `OS に拒否されました (${detail})`,
      keepAwakeUnsupported: "このプラットフォームではスリープ抑止に対応していません",
    },
    firmwareUnread:
      "デバイスのファームウェアバージョンを読み取れず、このビルドのパラメーターマッピングが実機に適合するか確認できません。接続し直してからやり直してください。",
    liveReadIncomplete: (n: number): string =>
      `${n} 件の設定を読み取れず、デバイスの状態を完全には把握できません。Live sync の開始には完全な読み取りが必要です。`,
    liveFollowStopped:
      "セッション開始中にデバイス追従が停止したため、実機側の変更がプランに届かない状態でした。Live sync は開始していません。",
    liveSyncStopped:
      "セッション開始中にデバイスへの送信が停止したため、編集が実機に届かない状態でした。Live sync は開始していません。",
    followReadIncomplete: (n: number): string =>
      `デバイス側の変更後、${n} 件の設定を読み戻せず、プランが実機と一致しなくなりました。取得し直して同期してください。`,
    clockUnread: (message: string): string =>
      `デバイスのサンプルレートと Follow USB の状態を読み取れませんでした (${message})。計画のレートを実機と照合していないため、何も書き込んでいません。`,
    playbackUnread: (message: string): string =>
      `microSD レコーダーが再生中・録音中かどうかを読み取れませんでした (${message})。本体はどちらの間もサンプリング周波数を変更させないため、何も書き込んでいません。`,
    rateDuringPlayback: (deviceRate: string, planRate: string): string =>
      `microSD レコーダーの再生中・一時停止中は、本体のサンプリング周波数を ${deviceRate} から ${planRate} に変更できません。本体で再生を停止してから、もう一度書き込んでください。何も書き込んでいません。`,
    rateDuringPlaybackLive: (deviceRate: string, planRate: string): string =>
      `microSD レコーダーの再生中・一時停止中は、本体のサンプリング周波数を ${deviceRate} から ${planRate} に変更できません。本体で再生を停止してから、もう一度レートを選んでください。何も書き込んでいません。`,
    rateDuringRecording: (deviceRate: string, planRate: string): string =>
      `microSD レコーダーの録音中・録音の一時停止中・録音待機中は、本体のサンプリング周波数を ${deviceRate} から ${planRate} に変更できません。本体で録音を停止するか待機を解除してから、もう一度書き込んでください。何も書き込んでいません。`,
    rateDuringRecordingLive: (deviceRate: string, planRate: string): string =>
      `microSD レコーダーの録音中・録音の一時停止中・録音待機中は、本体のサンプリング周波数を ${deviceRate} から ${planRate} に変更できません。本体で録音を停止するか待機を解除してから、もう一度レートを選んでください。何も書き込んでいません。`,
    settingsClockDuringPlayback:
      "設定ファイルは本体のクロック (サンプリング周波数か Follow USB) を変えるため、microSD レコーダーの再生中・一時停止中は取り込みません。本体で再生を停止してから、もう一度取り込んでください。何も取り込んでいません。",
    settingsClockDuringRecording:
      "設定ファイルは本体のクロック (サンプリング周波数か Follow USB) を変えるため、microSD レコーダーの録音中・録音の一時停止中・録音待機中は取り込みません。本体で録音を停止するか待機を解除してから、もう一度取り込んでください。何も取り込んでいません。",
    trackCountUnread: (message: string): string =>
      `microSD レコーダーの Track Count を読み取れませんでした (${message})。レート変更はこの値を元に戻せない形で下げることがあるため、何も書き込んでいません。`,
    followUsbWrite: (message: string, on = false): string =>
      `Follow USB を ${on ? "ON" : "OFF"} にできませんでした (${message})。何も書き込んでいません。`,
    unknownModel: (model: string): string => `未知の機種: ${model}`,
    modelMismatch: (device: string, ui: string): string =>
      `接続中のデバイスは ${device} ですが、${ui} を選択中です。先にその機種の計画を開くか切り替えてください。`,
    notPlanFile: "URX Router の計画ファイルではありません",
    urxf: {
      notUrxf: "URX の設定ファイルではありません",
      truncated: "設定ファイルが途中で切れているか、想定外のレコードがあります",
      badBlock: "設定ファイルのブロック構造を読み取れません",
      badDescriptor: "このビルドが解釈できないパラメータが設定ファイルに含まれています",
      lengthMismatch: "設定ファイルのパラメータ表と値が一致しません (ファイルが破損しています)",
      noCurrent: "設定ファイルに現在の設定が含まれていません (保存済みシーンのみ)",
    },
    missingModel: "計画ファイルに modelId がありません",
    badPlanUrl: "計画リンクが不正です (デコードできません)",
    planUrlUnsupported:
      "このブラウザは圧縮された計画リンクに対応していません — 新しい Chrome / Edge / Firefox、または Safari 16.4 以降をご利用ください",
    planVersionUnsupported:
      "この計画は新しいバージョンの URX Router で保存されています — アプリを更新してから開いてください",
  },
};
